import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/**
 * KeycapButton — the raised "offset key" in the hex-notch silhouette (the redo's
 * primary Button). A solid notched block sits 3px down-right BEHIND the key (the
 * "travel path"); the key presses flush onto it on `:active` (it translates the
 * 3px). Because the block is a real sibling element (not a `box-shadow`, which a
 * `clip-path` slices, nor a `drop-shadow`, which reads faint on the notch), the
 * dark travel path is unmistakable and survives the notch.
 *
 * - `primary` — rich navy fill (a subtle top-lit gradient), the main CTA.
 * - `secondary` — surface face; `destructive` — red. All three are keyed (block).
 * - `outline` — flat bordered, transparent (ring only, no travel block).
 * - `ghost` — quiet text, soft hover (no ring, no block).
 * - The 1px ink border is an `nx-rim` RING (NX_RING_CLIP) so it traces the notch
 *   instead of being sliced at the diagonal (#158); every variant but ghost has it.
 * - `notch` picks the chamfer (`t` subtlest → `l` largest); default `b`.
 * `asChild` renders the consumer's element (e.g. an `<a>`) as the key face.
 */
const keycapVariants = cva(
  [
    "group/nxbtn relative z-[1] inline-flex select-none cursor-pointer items-center justify-center gap-2 whitespace-nowrap border-0",
    "font-semibold leading-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    // Clip-safe focus: a drop-shadow glow SURVIVES the notch clip-path, where a
    // `ring-*` box-shadow would be sliced at the chamfer (onyx-material.ts rule #2).
    // NotchedInput's exact glow weight, eased below with the press ride - the
    // whole key family focuses like the form family (ui#229 pair).
    "focus-visible:outline-none focus-visible:[filter:drop-shadow(0_0_3px_hsl(var(--ring)/0.45))]",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        // Rich navy: a subtle top-lit gradient (navy lightened toward white at the
        // top → navy) for depth, derived from --primary so it stays token-driven.
        primary:
          "text-primary-foreground bg-[linear-gradient(176deg,color-mix(in_oklab,hsl(var(--primary))_84%,#fff),hsl(var(--primary)))]",
        // Surface face; theme-flipping tokens so it adapts light/dark.
        secondary: "bg-background text-foreground",
        // Destructive red.
        destructive: "bg-destructive text-destructive-foreground",
        // Flat bordered, transparent fill (ring only, no travel block).
        outline: "bg-transparent text-foreground",
        // Quiet — no ring, no block; soft hover.
        ghost: "bg-transparent text-foreground hover:bg-foreground/[0.06]",
      },
      size: {
        sm: "h-9 px-4 text-[13px]",
        default: "h-11 px-5 text-sm",
        lg: "h-12 px-7 text-[15px] tracking-[0.2px]",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

type KeycapElementProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">;

export interface KeycapButtonProps extends KeycapElementProps, VariantProps<typeof keycapVariants> {
  /** Notch chamfer size. Default `b` (refined for a button). */
  notch?: NxSize;
  /** Render the consumer's child element via Radix Slot (e.g. an `<a>`). */
  asChild?: boolean;
}

export const KeycapButton = React.forwardRef<HTMLButtonElement, KeycapButtonProps>(
  (
    { className, variant = "primary", size, notch = "b", asChild = false, children, type, ...rest },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isGhost = variant === "ghost";
    // Filled variants are "keyed": they get the solid travel-path block. Primary
    // is raised MORE (travels 3px); secondary/destructive are simpler, less raised
    // (2px). Outline + ghost are transparent (a block would show through) so they
    // stay flat — but still press DOWN 1px, so every button has depth + the same
    // springy bounce (no box-shadow needed for the quiet ones, just the press).
    const raisedMore = variant === "primary";
    const keyed = raisedMore || variant === "secondary" || variant === "destructive";
    const press = raisedMore
      ? "active:translate-x-[3px] active:translate-y-[3px]"
      : keyed
        ? "active:translate-x-[2px] active:translate-y-[2px]"
        : "active:translate-y-px";
    // Springy bounce on the release (overshoot ease) — applies to every variant —
    // composed per-property with the 180ms smooth-out focus-glow ride (two
    // transition-* utilities would fight over transition-property; the Button
    // precedent, ui#229).
    const bounce =
      "[transition:transform_150ms_cubic-bezier(0.34,1.45,0.6,1),filter_180ms_cubic-bezier(0.16,1,0.3,1)]";
    // A disabled key fades via the face's cva `disabled:opacity-50`; dim the
    // sibling travel block in lockstep so it never reads as half-lifted (an opaque
    // dark block behind a 50%-faded face). #179 review.
    const isDisabled = rest.disabled ?? false;

    const face = (
      <Comp
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(
          nx[notch],
          keycapVariants({ variant, size }),
          // Springy press for EVERY variant (the bounce); the travel distance
          // varies by raise level (3px primary / 2px secondary / 1px quiet).
          bounce,
          press,
          // Keyed variants wrap in a sizing span (below); the face fills it so a
          // consumer width utility (w-full/flex-1) applied to the wrapper reaches
          // the key. Non-keyed variants ARE the root, so they take `className` here.
          keyed ? "w-full" : className,
        )}
        type={asChild ? undefined : (type ?? "button")}
        {...rest}
      >
        {/* Ink keyline — a hollow ring on the SAME notch (NX_RING_CLIP) so the 1px
            border traces the notch instead of being sliced at the diagonal (#158).
            From `--foreground` (navy light / white dark) so it stays neutral. */}
        {!isGhost && (
          <span
            aria-hidden
            className={cn(
              "nx-rim pointer-events-none absolute inset-0",
              // Ink at rest -> --ring on the key's focus, eased: the same
              // border-anim the Button + NotchedInput rims carry (ui#229 pair).
              // Rest color is a CLASS so the focus override can win.
              "[--nx-rim:hsl(var(--foreground))]",
              "transition-colors duration-180 ease-smooth-out",
              "group-focus-visible/nxbtn:[--nx-rim:hsl(var(--ring))]",
            )}
            style={{ clipPath: NX_RING_CLIP[notch] } as React.CSSProperties}
          />
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );

    if (!keyed) return face;

    // The travel path: a solid notched block 3px down-right, BEHIND the key. It's
    // a sibling (not a child) so it stays put while the key presses onto it. Dark
    // ink so it reads on the light page; a deep accent in dark.
    return (
      <span className={cn("relative inline-flex", className)}>
        <span
          aria-hidden
          className={cn(
            nx[notch],
            "pointer-events-none absolute inset-0 z-0 bg-[#06101e] dark:bg-[#0a2a5e]",
            // Primary travels 3px (more raised); secondary/destructive 2px (less).
            raisedMore
              ? "translate-x-[3px] translate-y-[3px]"
              : "translate-x-[2px] translate-y-[2px]",
            isDisabled && "opacity-50",
          )}
        />
        {face}
      </span>
    );
  },
);

KeycapButton.displayName = "KeycapButton";
